import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTeam } from 'shared/context/teamContext';
import axiosInstance from 'shared/services/api/axiosInstance';
import UsersFilterModule from 'features/activity/components/UsersFilterModule';
import Icon from 'shared/components/Icon';
import ConfirmModal from 'features/analytics/studio/components/boards/ConfirmModal';
import { trackClientEvent } from 'shared/utils/trodoEvents';
import { useBreadcrumbActions } from 'shared/context/BreadcrumbContext';
import { settingsCardShell } from 'features/settings/components/settingsTheme';
import { X } from 'lucide-react';

/** Table date like "Apr 8, 2026" (reference UI) */
function formatTableDate(date) {
  if (!date) return '—';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Resolve a count from the API map.
 *
 * Property access coerces the key to a string, so a single lookup already
 * covers both the numeric and string forms — the previous Number()/String()
 * retries checked the identical key three times.
 */
function cohortCountFromResponse(counts, cohortId) {
  if (counts == null || typeof counts !== 'object' || cohortId == null || cohortId === '') {
    return undefined;
  }
  return Object.prototype.hasOwnProperty.call(counts, cohortId) ? counts[cohortId] : undefined;
}

function CohortsSkeleton() {
  const bar = 'animate-pulse rounded bg-[color:var(--surface-hover)]';
  return (
    <div className={settingsCardShell} aria-busy="true">
      <span className="sr-only">Loading cohorts…</span>
      <div className="divide-y divide-[color:var(--border-subtle)]">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 px-3.5 py-3">
            <div className={`${bar} h-3.5 w-3.5 shrink-0 rounded-sm`} />
            <div className={`${bar} h-3 flex-1`} />
            <div className={`${bar} h-3 w-12 shrink-0`} />
            <div className={`${bar} h-3 w-20 shrink-0`} />
            <div className={`${bar} h-3 w-[30%] shrink-0`} />
          </div>
        ))}
      </div>
    </div>
  );
}

const DESC_SAVE_DEBOUNCE_MS = 650;

const cohortCheckboxClass =
  'h-4 w-4 rounded border-[color:var(--border-default)] accent-[color:var(--brand-primary)] focus:ring-2 focus:ring-[color:var(--brand-primary)] focus:ring-offset-0';

/**
 * Inline description: click to open textarea in the row; debounced PUT + save on blur.
 */
function CohortDescriptionCell({ cohort, onCohortUpdated }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const debounceRef = useRef(null);
  const lastSavedRef = useRef('');
  const textareaRef = useRef(null);

  const serverDesc = cohort.description != null ? String(cohort.description) : '';

  useEffect(() => {
    if (!editing) {
      lastSavedRef.current = serverDesc;
    }
  }, [serverDesc, editing]);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const persist = useCallback(
    async (value) => {
      if (value === lastSavedRef.current) return true;
      setSaving(true);
      setSaveError(null);
      try {
        const { data } = await axiosInstance.put(`/cohorts/${cohort.id}`, { description: value });
        const updated = data?.cohort;
        lastSavedRef.current = value;
        if (updated && onCohortUpdated) {
          onCohortUpdated(cohort.id, updated);
        }
        return true;
      } catch (err) {
        setSaveError(err.response?.data?.message || 'Could not save');
        return false;
      } finally {
        setSaving(false);
      }
    },
    [cohort.id, onCohortUpdated]
  );

  const scheduleSave = useCallback(
    (value) => {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        persist(value);
      }, DESC_SAVE_DEBOUNCE_MS);
    },
    [persist]
  );

  const startEdit = () => {
    const d = serverDesc;
    setDraft(d);
    lastSavedRef.current = d;
    setSaveError(null);
    setEditing(true);
  };

  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [editing]);

  const handleBlur = async () => {
    clearTimeout(debounceRef.current);
    const v = draft;
    if (v !== lastSavedRef.current) {
      const ok = await persist(v);
      if (!ok) return;
    }
    setEditing(false);
  };

  const trimmedDesc = (cohort.description || '').trim();
  const hasDescription = trimmedDesc.length > 0;

  if (editing) {
    return (
      <div className="min-w-[12rem] max-w-md">
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => {
            const v = e.target.value;
            setDraft(v);
            scheduleSave(v);
          }}
          onBlur={handleBlur}
          onClick={(e) => e.stopPropagation()}
          rows={3}
          className="w-full rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] px-2 py-1.5 text-[13px] font-[450] leading-snug text-[color:var(--text-primary)] placeholder:text-[color:var(--text-disabled)] focus:outline-none focus:ring-0 focus:border-[color:var(--text-primary)] resize-y"
          style={{ resize: 'vertical', minHeight: '4.5rem' }}
          placeholder="Optional notes for your team"
          aria-label={`Description for ${cohort.name}`}
        />
        {(saving || saveError) && (
          <div className={`mt-1 text-xs ${saveError ? 'text-danger' : 'text-[color:var(--text-muted)]'}`}>
            {saveError || 'Saving…'}
          </div>
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        startEdit();
      }}
      className="group relative w-full max-w-md text-left rounded-md px-1 py-0.5 -mx-1 transition-colors focus:outline-none focus:ring-2 focus:ring-[color:var(--brand-primary)] focus:ring-offset-1 text-[color:var(--text-muted)]"
      title={hasDescription ? 'Click to edit description' : 'Add description'}
      aria-label={hasDescription ? `Edit description: ${cohort.name}` : `Add description for ${cohort.name}`}
    >
      {hasDescription ? (
        <span className="block line-clamp-2 break-words rounded px-1 -mx-1 py-0.5 min-h-[1.25rem] group-hover:bg-surface-hover/80 group-hover:text-[color:var(--text-primary)]">
          {trimmedDesc}
        </span>
      ) : (
        <span
          className="pointer-events-none block min-h-[1.25rem] w-full truncate rounded px-1 -mx-1 py-0.5 text-[12px] text-[color:var(--text-disabled)] transition-colors group-hover:bg-surface-hover/80 group-hover:text-[color:var(--text-muted)]"
          aria-hidden
        >
          Add description…
        </span>
      )}
    </button>
  );
}

/* ─────────────────────────────────────────────────────────────────
   CohortModal: redesigned shell, original filter logic
───────────────────────────────────────────────────────────────── */
const INPUT_CLS = 'w-full rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] px-3 py-2 text-[13px] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] outline-none transition-colors focus:border-[color:var(--brand-primary)]';

const CohortModal = ({ isOpen, onClose, teamId, cohort, onSaved }) => {
  const [filters, setFilters] = useState([]);
  const [cohortName, setCohortName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const nameRef = useRef(null);
  /**
   * Conditions the builder can't render (transaction- and cohort-sourced).
   * They're held aside rather than dropped: save PUTs the whole `filters`
   * array, so excluding them from the editable set used to delete them from
   * the cohort permanently — just by opening it and pressing Save.
   */
  const [preservedFilters, setPreservedFilters] = useState([]);

  const isEdit = !!cohort;

  useEffect(() => {
    if (!isOpen) return;
    setCohortName(cohort?.name || '');
    setError(null);
    const raw = cohort?.filters || [];
    const isEditable = (f) => {
      const src = f.propertySource || f.category;
      return src !== 'transaction' && src !== 'cohort';
    };
    setFilters(raw.filter(isEditable));
    setPreservedFilters(raw.filter((f) => !isEditable(f)));
    setTimeout(() => nameRef.current?.focus(), 50);
  }, [isOpen, cohort]);

  const handleSave = async () => {
    const name = (cohortName || '').trim();
    if (!name) { setError('Please enter a cohort name'); return; }
    if (!teamId) { setError('No team selected'); return; }
    setSaving(true);
    setError(null);
    // Merge back the conditions the builder can't render, so saving edits the
    // cohort rather than truncating it.
    const nextFilters = [...filters, ...preservedFilters];
    try {
      if (isEdit) {
        await axiosInstance.put(`/cohorts/${cohort.id}`, { name, filters: nextFilters });
        trackClientEvent('cohort_updated', { cohort_id: cohort.id, filter_count: nextFilters.length }, { category: 'product' });
      } else {
        await axiosInstance.post('/cohorts', { team_id: teamId, name, filters: nextFilters });
        trackClientEvent('cohort_created', { filter_count: nextFilters.length }, { category: 'product' });
      }
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save cohort');
    } finally {
      setSaving(false);
    }
  };

  // Escape / backdrop shouldn't silently bin a half-built cohort.
  const isDirty =
    (cohortName || '').trim() !== ((cohort?.name || '').trim())
    || JSON.stringify(filters) !== JSON.stringify(
      (cohort?.filters || []).filter((f) => {
        const src = f.propertySource || f.category;
        return src !== 'transaction' && src !== 'cohort';
      }),
    );

  const requestClose = useCallback(() => {
    if (saving) return;
    if (isDirty && !window.confirm('Discard your changes to this cohort?')) return;
    onClose();
  }, [saving, isDirty, onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKey = (e) => {
      if (e.key !== 'Escape') return;
      // The filter builder opens its own popover — let that close first rather
      // than tearing down the whole modal underneath it.
      if (document.querySelector('[data-filter-popover-open]')) return;
      requestClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, requestClose]);

  // Lock background scroll while open (ConfirmModal already does this).
  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  // Send focus back where it came from on close.
  useEffect(() => {
    if (!isOpen) return undefined;
    const opener = document.activeElement;
    return () => {
      if (opener && typeof opener.focus === 'function') opener.focus();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'var(--surface-overlay)', backdropFilter: 'blur(2px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) requestClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cohort-modal-title"
        className="relative flex w-full max-w-4xl flex-col rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-raised)]"
        style={{ maxHeight: '92vh', boxShadow: 'var(--shadow-lg)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="flex shrink-0 items-center justify-between gap-4 border-b border-[color:var(--border-default)] px-6 py-4">
          <div>
            <h2 id="cohort-modal-title" className="text-[15px] font-[450] tracking-tight text-[color:var(--text-primary)]">
              {isEdit ? 'Edit cohort' : 'New cohort'}
            </h2>
            <p className="mt-0.5 text-[12px] text-[color:var(--text-muted)]">
              {isEdit ? 'Update the name or filter conditions.' : 'Define a reusable segment of users.'}
            </p>
          </div>
          <button
            type="button"
            onClick={requestClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--surface-hover)] hover:text-[color:var(--text-primary)]"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* ── Scrollable body ── */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {error && (
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-[color:var(--feedback-error)] bg-danger-soft px-4 py-2.5 text-[13px] text-danger">
              <X size={14} className="shrink-0" />
              {error}
            </div>
          )}

          {/* Name */}
          <div className="mb-5">
            <label htmlFor="cohort-name" className="mb-1.5 block text-[12px] font-medium text-[color:var(--text-muted)]">
              Cohort name
            </label>
            <input
              id="cohort-name"
              ref={nameRef}
              type="text"
              value={cohortName}
              onChange={(e) => setCohortName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) handleSave(); }}
              placeholder="e.g. Power Users, High Value"
              className={INPUT_CLS}
            />
          </div>

          {/* Filters */}
          <div>
            <label className="mb-2 block text-[12px] font-medium text-[color:var(--text-muted)]">
              Conditions
            </label>
            <UsersFilterModule
              filters={filters}
              onFiltersChange={setFilters}
              teamId={teamId}
              cohortBuilder
            />
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-[color:var(--border-default)] px-6 py-4">
          <span className="text-[12px] text-[color:var(--text-muted)]">
            {filters.length === 0 && preservedFilters.length === 0
              ? 'No conditions: cohort will match all users'
              : `${filters.length} condition${filters.length === 1 ? '' : 's'}`}
            {preservedFilters.length > 0 && (
              <>
                {' '}
                <span title="Transaction and cohort conditions aren't editable here, but they're kept when you save.">
                  (+{preservedFilters.length} not editable here)
                </span>
              </>
            )}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={requestClose}
              className="rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] px-4 py-2 text-[13px] font-medium text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--surface-hover)]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !(cohortName || '').trim()}
              className="inline-flex items-center gap-2 rounded-lg bg-brand px-5 py-2 text-[13px] font-medium text-ink-onBrand transition-colors hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? (
                <>
                  <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-ink-onBrand border-t-transparent" />
                  Saving…
                </>
              ) : (
                isEdit ? 'Update cohort' : 'Save cohort'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const CohortsPanel = () => {
  const { selectedTeam } = useTeam();
  const teamId = useMemo(() => {
    if (selectedTeam?._id) return selectedTeam._id;
    if (selectedTeam?.id) return selectedTeam.id;
    try {
      const td = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
      return td._id || td.id || null;
    } catch {
      return null;
    }
  }, [selectedTeam]);

  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [countsLoading, setCountsLoading] = useState(false);
  const [countsFailed, setCountsFailed] = useState(false);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCohort, setEditingCohort] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const selectAllRef = useRef(null);

  /**
   * The list and the counts are separate endpoints (counts are cached server
   * side with a long TTL) — so render the table as soon as the list lands and
   * fill the counts in after, rather than holding the whole page behind the
   * slower call. Previously both flags flipped together, which made the
   * per-row "…" state unreachable: whenever counts were loading, the page
   * spinner was covering the table anyway.
   */
  const fetchCohorts = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    setError(null);
    let listed = [];
    try {
      const listRes = await axiosInstance.get('/cohorts', { params: { team_id: teamId } });
      listed = listRes.data?.cohorts || [];
      setCohorts(listed.map((c) => ({ ...c, userCount: null })));
    } catch (err) {
      console.error('Error fetching cohorts:', err);
      setError(err.response?.data?.message || 'Failed to load cohorts');
      return;
    } finally {
      setLoading(false);
    }

    if (!listed.length) return;

    setCountsLoading(true);
    setCountsFailed(false);
    try {
      const countsRes = await axiosInstance.get('/cohorts/user-counts', {
        params: { team_id: teamId },
      });
      const counts = countsRes.data?.counts || {};
      setCohorts((prev) =>
        prev.map((c) => {
          const uc = cohortCountFromResponse(counts, c.id);
          return { ...c, userCount: uc !== undefined ? uc : null };
        }),
      );
    } catch (err) {
      // Non-fatal — the cohorts themselves are fine. But say so rather than
      // rendering "—" for every row, which is indistinguishable from "0 users".
      console.warn('[Cohorts] user-counts failed:', err?.response?.status, err?.message || err);
      setCountsFailed(true);
    } finally {
      setCountsLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    if (teamId) fetchCohorts();
  }, [teamId, fetchCohorts]);

  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => cohorts.some((c) => c.id === id)));
  }, [cohorts]);

  useEffect(() => {
    const el = selectAllRef.current;
    if (!el) return;
    const n = selectedIds.length;
    const total = cohorts.length;
    el.indeterminate = n > 0 && n < total;
  }, [selectedIds, cohorts.length]);

  const openCreateModal = useCallback(() => {
    setEditingCohort(null);
    setModalOpen(true);
  }, []);

  // Inject "Add cohort" button into the GlobalTopBar right-side slot
  useBreadcrumbActions(
    <button
      type="button"
      onClick={openCreateModal}
      className="inline-flex h-6 items-center gap-1 rounded-md bg-brand px-2.5 text-[12px] font-medium text-ink-onBrand transition-colors hover:bg-brand-hover"
    >
      <Icon name="add_plus" size={12} />
      Add cohort
    </button>
  );


  const openEditModal = (cohort) => {
    setEditingCohort(cohort);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingCohort(null);
  };

  const allSelected = cohorts.length > 0 && selectedIds.length === cohorts.length;
  const someSelected = selectedIds.length > 0;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(cohorts.map((c) => c.id));
    }
  };

  const toggleRow = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  /** Distinguish "still counting" from "count unavailable" from a real 0. */
  const formatCount = (n) => {
    if (n === null || n === undefined) {
      if (countsLoading) return '…';
      return countsFailed ? 'n/a' : '—';
    }
    const num = typeof n === 'number' ? n : Number(n);
    if (!Number.isNaN(num)) return num.toLocaleString();
    return '—';
  };

  const selectedCohorts = useMemo(
    () => cohorts.filter((c) => selectedIds.includes(c.id)),
    [cohorts, selectedIds]
  );

  const openBulkDeleteModal = () => {
    if (selectedIds.length === 0) return;
    setDeleteModalOpen(true);
  };

  const confirmBulkDelete = async () => {
    if (selectedIds.length === 0 || deleting) return;
    setDeleting(true);
    setError(null);
    try {
      // allSettled, not all: `all` rejects on the first failure, so any cohorts
      // that HAD been deleted stayed on screen until a manual reload. Always
      // refetch, then report only what actually failed.
      const results = await Promise.allSettled(
        selectedIds.map((id) => axiosInstance.delete(`/cohorts/${id}`)),
      );
      const failed = results.filter((r) => r.status === 'rejected');
      setDeleteModalOpen(false);
      setSelectedIds([]);
      await fetchCohorts();
      if (failed.length) {
        setError(
          failed.length === selectedIds.length
            ? 'Failed to delete the selected cohorts.'
            : `Deleted ${selectedIds.length - failed.length} of ${selectedIds.length}; the rest could not be deleted.`,
        );
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete one or more cohorts');
    } finally {
      setDeleting(false);
    }
  };

  const bulkDeleteMessage = useMemo(() => {
    const n = selectedIds.length;
    if (n === 0) return '';
    const names = selectedCohorts.map((c) => c.name).filter(Boolean);
    if (names.length === 0) {
      return `Delete ${n} cohort${n === 1 ? '' : 's'}? This cannot be undone.`;
    }
    const preview = names.slice(0, 3).join(', ');
    const extra = names.length > 3 ? ` and ${names.length - 3} more` : '';
    return `Delete ${n} cohort${n === 1 ? '' : 's'} (${preview}${extra})? This cannot be undone.`;
  }, [selectedIds.length, selectedCohorts]);

  const handleCohortDescriptionUpdated = useCallback((id, apiCohort) => {
    setCohorts((prev) =>
      prev.map((c) =>
        c.id === id
          ? {
              ...c,
              description: apiCohort.description != null ? apiCohort.description : '',
              updatedAt: apiCohort.updatedAt ?? c.updatedAt
            }
          : c
      )
    );
  }, []);

  if (!teamId) {
    return (
      <div className="py-12 text-center w-full text-[color:var(--text-muted)]">
        Please select a team to manage cohorts.
      </div>
    );
  }


  return (
    <div className="w-full space-y-3">
      {someSelected && (
        <div className="flex items-center gap-2">
          <span className="text-[12px] text-[color:var(--text-muted)]">
            {selectedIds.length} selected
          </span>
          <button
            type="button"
            onClick={() => setSelectedIds([])}
            className="rounded-md px-2 py-0.5 text-[12px] text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)]"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={openBulkDeleteModal}
            className="inline-flex items-center gap-1.5 rounded-md border border-[color:var(--feedback-error)] bg-danger-soft px-2.5 py-1 text-[12px] font-medium text-danger transition-colors hover:bg-danger hover:text-ink-onBrand"
          >
            <Icon name="delete" size={12} color="currentColor" />
            Delete {selectedIds.length}
          </button>
        </div>
      )}

      {error && (
        <div className="rounded-md border border-[color:var(--feedback-error)] bg-danger-soft px-3 py-2 text-[13px] text-danger">
          {error}
        </div>
      )}

      {loading ? (
        <CohortsSkeleton />
      ) : cohorts.length === 0 ? (
        <div className={`${settingsCardShell} px-3.5 py-10 text-center`}>
          <p className="text-[13px] font-[450] text-[color:var(--text-primary)]">No cohorts yet</p>
          <p className="mx-auto mt-1 max-w-sm text-[11px] leading-snug text-[color:var(--text-muted)]">
            A cohort is a reusable segment of users you can filter and analyse by.
          </p>
          {/* Inline CTA: the top-bar "Add cohort" button lives in a different
              subtree, so pointing at it from here was disorienting. */}
          <button
            type="button"
            onClick={openCreateModal}
            className="mt-3 inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[11px] font-medium text-[color:var(--text-on-brand)] transition-opacity hover:opacity-90"
            style={{ backgroundColor: 'var(--brand-primary)' }}
          >
            <Icon name="add_plus" size={12} />
            Create a cohort
          </button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface">
          {/* Header. Hidden on mobile, where each row stacks instead. */}
          <div className="hidden h-8 items-center gap-3 border-b border-[color:var(--border-default)] px-3 text-[10.5px] font-semibold uppercase tracking-wider text-[color:var(--text-muted)] whitespace-nowrap sm:flex">
            <span className="flex w-4 shrink-0 items-center">
              <input
                ref={selectAllRef}
                type="checkbox"
                checked={allSelected}
                onChange={toggleSelectAll}
                className={cohortCheckboxClass}
                aria-label="Select all cohorts"
              />
            </span>
            <span className="min-w-0 flex-1">Cohort</span>
            <span className="w-20 shrink-0 text-right">Users</span>
            <span className="w-28 shrink-0">Last edited</span>
            <span className="w-[34%] shrink-0">Description</span>
          </div>

          <div className="divide-y divide-[color:var(--border-subtle)]">
            {cohorts.map((cohort) => {
              const checked = selectedIds.includes(cohort.id);
              return (
                <div
                  key={cohort.id}
                  className="flex flex-col gap-2 px-3 py-2.5 transition-colors hover:bg-surface-hover sm:min-h-8 sm:flex-row sm:items-center sm:gap-3 sm:py-0"
                >
                  <span className="flex w-4 shrink-0 items-center">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleRow(cohort.id)}
                      className={cohortCheckboxClass}
                      aria-label={`Select ${cohort.name}`}
                    />
                  </span>

                  <span className="min-w-0 flex-1">
                    {/* --brand-primary on white is 2.78:1 at this size, which
                        fails AA: the name is the primary action on the row, so
                        it reads as body text and announces itself by hover. */}
                    <button
                      type="button"
                      onClick={() => openEditModal(cohort)}
                      className="max-w-full truncate rounded text-left text-[12px] font-[450] text-[color:var(--text-primary)] underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]"
                    >
                      {cohort.name}
                    </button>
                  </span>

                  <span
                    className="shrink-0 text-[12px] tabular-nums text-[color:var(--text-primary)] sm:w-20 sm:text-right"
                    title={countsFailed ? "Couldn't load user counts" : undefined}
                  >
                    {formatCount(cohort.userCount)}
                  </span>

                  <span className="shrink-0 whitespace-nowrap text-[11px] text-[color:var(--text-muted)] sm:w-28">
                    {formatTableDate(cohort.updatedAt)}
                  </span>

                  <span className="min-w-0 text-[12px] sm:w-[34%] sm:shrink-0">
                    <CohortDescriptionCell cohort={cohort} onCohortUpdated={handleCohortDescriptionUpdated} />
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <CohortModal
        isOpen={modalOpen}
        onClose={closeModal}
        teamId={teamId}
        cohort={editingCohort}
        onSaved={fetchCohorts}
      />

      <ConfirmModal
        open={deleteModalOpen}
        danger
        title="Delete cohorts"
        message={bulkDeleteMessage}
        confirmLabel={deleting ? 'Deleting...' : 'Delete'}
        cancelLabel="Cancel"
        onConfirm={confirmBulkDelete}
        onCancel={() => {
          if (deleting) return;
          setDeleteModalOpen(false);
        }}
      />
    </div>
  );
};

export default CohortsPanel;
